import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Dayjs } from "dayjs";
import DataFilter from "../../components/common/DataFilter";
import { PresetKey, presets, resolveRangeFromParams } from "../../components/common/dataFilterPresets";
import DisputesMetrics from "../../components/dashboard/DisputesMetrics";
import AlertsMetrics from "../../components/dashboard/AlertsMetrics";
import { useCompanyId } from "../../hooks/useCompanyId";
import { useBrandId } from "../../hooks/useBrandId";
import { useDisputesVisible } from "../../hooks/useDisputesAccess";
// import MonthlyDisputesChart from "../../components/dashboard/MonthlyDisputesChart";
// import MonthlyDisputesChartV2 from "../../components/dashboard/MonthlyDisputesChartV2";

export type Currency = "USD" | "EUR";
export type DashboardTab = "overall" | "paypal" | "byBrand" | "byAccount";
export const CURRENCY_STORAGE_KEY = "selectedCurrency";
export const ASSIGNED_ONLY_STORAGE_KEY = "dashboardAssignedOnly";

export default function OverviewTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyId } = useCompanyId();
  const { brandId } = useBrandId();
  const disputesVisible = useDisputesVisible();

  const initialRange = resolveRangeFromParams(searchParams, presets, "30D");

  const [preset, setPreset] = useState<PresetKey>(initialRange.preset);
  const [startDate, setStartDate] = useState<Dayjs | null>(initialRange.start);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialRange.end);

  // Initialize assignedOnly from localStorage
  const [assignedOnly, setAssignedOnly] = useState<boolean>(() => {
    return localStorage.getItem(ASSIGNED_ONLY_STORAGE_KEY) === "true";
  });

  // Initialize currency from localStorage
  const [currency, setCurrency] = useState<Currency>(() => {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
    return (stored === "USD" || stored === "EUR") ? stored : "USD";
  });

  // Tab state
  const initialTab = searchParams.get("tab") as DashboardTab;
  const [activeTab, setActiveTab] = useState<DashboardTab>(
    initialTab === "paypal" || initialTab === "byBrand" || initialTab === "byAccount" ? initialTab : "overall"
  );
  const [hasPaypalAccount, setHasPaypalAccount] = useState(false);
  const [hasMultipleBrands, setHasMultipleBrands] = useState(false);
  const [hasMultiplePpAccounts, setHasMultiplePpAccounts] = useState(false);

  const handleTabsAvailable = useCallback((tabs: { hasPaypalAccount: boolean; hasMultipleBrands: boolean; hasMultiplePpAccounts: boolean }) => {
    setHasPaypalAccount(tabs.hasPaypalAccount);
    setHasMultipleBrands(tabs.hasMultipleBrands);
    setHasMultiplePpAccounts(tabs.hasMultiplePpAccounts);
    if (!tabs.hasPaypalAccount && activeTab === "paypal") {
      setActiveTab("overall");
    }
    if (!tabs.hasMultipleBrands && activeTab === "byBrand") {
      setActiveTab("overall");
    }
    if (!tabs.hasMultiplePpAccounts && activeTab === "byAccount") {
      setActiveTab("overall");
    }
  }, [activeTab]);

  // When admin/agent changes company selection, reset to overall before render
  const prevCompanyId = useRef(companyId);
  if (prevCompanyId.current !== companyId) {
    prevCompanyId.current = companyId;
    if (activeTab !== "overall") {
      setActiveTab("overall");
    }
  }

  // Sync localStorage when currency changes
  useEffect(() => {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
  }, [currency]);

  // Sync localStorage when assignedOnly changes
  useEffect(() => {
    localStorage.setItem(ASSIGNED_ONLY_STORAGE_KEY, String(assignedOnly));
  }, [assignedOnly]);

  // Update URL whenever date filter changes (companyId is handled by CmLink navigation)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("range", preset);
    if (startDate) params.set("start", startDate.format("YYYY-MM-DD"));
    if (endDate) params.set("end", endDate.format("YYYY-MM-DD"));
    if (activeTab !== "overall") {
      params.set("tab", activeTab);
    } else {
      params.delete("tab");
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, startDate, endDate, activeTab]);

  const handlePresetChange = (newPreset: PresetKey) => {
    setPreset(newPreset);
    if (newPreset !== "custom") {
      setStartDate(presets[newPreset].start());
      setEndDate(presets[newPreset].end());
    }
  };

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue);
    setPreset("custom");
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    setEndDate(newValue);
    setPreset("custom");
  };

  const handleCurrencyChange = (newCurrency: Currency) => {
    setCurrency(newCurrency);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as DashboardTab);
  };

  const handleDisputeViewChange = (value: 'all' | 'assigned') => {
    setAssignedOnly(value === 'assigned');
  };

  return (
    <div className="space-y-6">
      <DataFilter
            preset={preset}
            startDate={startDate}
            endDate={endDate}
            onPresetChange={handlePresetChange}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            currency={currency}
            onCurrencyChange={handleCurrencyChange}
            activeTab={activeTab}
            onActiveTabChange={handleTabChange}
            tabOptions={hasPaypalAccount || hasMultipleBrands || hasMultiplePpAccounts ? [
              { value: "overall", label: "Overall" },
              ...(hasMultiplePpAccounts ? [{ value: "byAccount", label: "Payment Processors" }] : []),
              ...(hasMultipleBrands ? [{ value: "byBrand", label: "Brands" }] : []),
              ...(hasPaypalAccount ? [{ value: "paypal", label: "PayPal" }] : []),
            ] : undefined}
            disputeView={assignedOnly ? "assigned" : "all"}
            onDisputeViewChange={handleDisputeViewChange}
          />
          {disputesVisible && (
            <DisputesMetrics
              startDate={startDate?.format("YYYY-MM-DD") ?? ""}
              endDate={endDate?.format("YYYY-MM-DD") ?? ""}
              companyId={companyId}
              brandId={brandId}
              currency={currency}
              activeTab={activeTab}
              assignedOnly={assignedOnly}
              onTabsAvailable={handleTabsAvailable}
            />
          )}
          <AlertsMetrics
            startDate={startDate?.format("YYYY-MM-DD") ?? ""}
            endDate={endDate?.format("YYYY-MM-DD") ?? ""}
            companyId={companyId}
            brandId={brandId}
            currency={currency}
            activeTab={activeTab}
          />
          {/* <h3>chart v1</h3>
          <MonthlyDisputesChart />
          <h3>chart v2</h3>
          <MonthlyDisputesChartV2 />
          <h3>chart v3</h3>
          <MonthlyDisputesChartV3 /> */}
    </div>
  );
}
